import React from 'react';
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Plus } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useConsumerRequests } from '../../../requests/hook';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useTranslation } from 'react-i18next';

export function ComplaintsTab({ consumerInfo }: { consumerInfo?: any }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {id}=useParams();

  const { data: apiData, isLoading } = useConsumerRequests({
    page: 1,
    limit: 100,
    consumer_id: id,
    remote_utility_id: getRemoteUtilityId(),
    request_type: "Complaint",
  });

  const complaints = React.useMemo(() => {
    if (!apiData?.results || apiData.results.length === 0) return [];
    return apiData.results.map((item: any, index: number) => ({
      keyId: item.id || item.requestNo || `complaint-${index}`,
      id:  item.id || t("N/A"),
      requestNo: item.requestNo || t("N/A"),
      status: item.statusDisplay || item.status || t("N/A"),
      priority: item.priorityDisplay || item.priority || t("Medium"),
      title: item.requestTypeDisplay || item.requestType || t("N/A"),
      date: item.requestDate || t("N/A"),
      description: item.consumerRemark || item.description || t("No description provided."),
    }));
  }, [apiData, t]);

  return (
    <div className="py-6">
      <div className="flex justify-end mb-4">
        <Button 
          onClick={() => {
            const params = new URLSearchParams({
              consumerId: consumerInfo?.selectedConsumerId?.toString() || "",
              isSubAccount: 'true'
            });
            navigate(`/dashboard/requests/create/complaint?${params.toString()}`);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-bold h-9 px-4 text-xs"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("Raise Complaint")}
        </Button>
      </div>
      
      <Accordion type="single" collapsible className="space-y-3">
        {complaints?.map((complaint: any) => (
          <AccordionItem 
            key={complaint.keyId} 
            value={complaint.keyId} 
            className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
          >
            <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-gray-50/50 transition-colors">
              <div className="flex flex-col text-left w-full pr-4">
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-bold text-[14px] text-gray-900">{complaint.requestNo}</span>
                  <Badge variant="secondary" className="bg-orange-50 text-orange-600 border-0 font-bold text-[11px] px-2 py-0.5 rounded-full">
                    {complaint.status}
                  </Badge>
                  <Badge variant="secondary" className="bg-red-50 text-red-600 border-0 font-bold text-[11px] px-2 py-0.5 rounded-full">
                    {complaint.priority}
                  </Badge>
                </div>
                <div className="flex flex-col text-[12px] font-medium text-gray-500">
                  <span>{complaint.title}</span>
                  <span className="text-gray-400 mt-0.5">{complaint.date}</span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-2 border-t border-gray-100 text-sm text-gray-600">
              <p>{complaint.description}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
